import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { format, parseISO } from 'date-fns'
import { Clock, Trash2, Plus, Timer, Play } from 'lucide-react'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { Skeleton } from '../components/ui/skeleton'
import { Button } from '../components/ui/button'
import { Dialog, DialogContent } from '../components/ui/dialog'
import { useTimerStore } from '../stores/timer-store'
import { primeAudio } from '../utils/timer-audio'
import type { ExerciseTemplate } from '../types/exercise-template'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog'
import { ResultRow } from '../components/training-diary/ResultRow'
import { workoutsService } from '../services/workouts'
import { workoutResultsService } from '../services/workout-results'
import { cn } from '../lib/utils'
import type { Workout, WorkoutResult } from '../types/workout'

function formatDuration(minutes: number, mPrefix: string, hPrefix: string): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}${mPrefix}`
  if (m === 0) return `${h}${hPrefix}`
  return `${h}${hPrefix} ${m}${mPrefix}`
}

export default function WorkoutDetailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { workoutId } = useParams<{ workoutId: string }>()

  const [workout, setWorkout] = useState<Workout | null>(null)
  const [results, setResults] = useState<WorkoutResult[]>([])
  const [loading, setLoading] = useState(true)
  const [descExpanded, setDescExpanded] = useState(false)
  const [imageOpen, setImageOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const armWorkout = useTimerStore((s) => s.armWorkout)
  const armTemplate = useTimerStore((s) => s.armTemplate)

  function handleArmFullWorkout() {
    if (!workout || !workout.exercise.templates || workout.exercise.templates.length === 0) return
    primeAudio()
    armWorkout({
      workoutId: workout.id,
      exercise: workout.exercise,
      templates: workout.exercise.templates,
    })
  }

  function handleArmTemplate(template: ExerciseTemplate) {
    if (!workout) return
    primeAudio()
    armTemplate({ workoutId: workout.id, exercise: workout.exercise, template })
  }

  const mPrefix = t('common.minutesShort', 'm')
  const hPrefix = t('common.hoursShort', 'h')

  const loadAll = useCallback(async () => {
    if (!workoutId) return
    setLoading(true)
    try {
      const w = await workoutsService.show(workoutId)
      setWorkout(w)
      const list = await workoutResultsService.list({ exerciseId: w.exercise.id })
      setResults(list)
    } catch {
      setWorkout(null)
    } finally {
      setLoading(false)
    }
  }, [workoutId])

  useEffect(() => { loadAll() }, [loadAll])

  // Breadcrumb: Logo | DD/MM (workout date or week start) | Exercise (muted, leaf)
  const dateShort = workout
    ? format(parseISO(workout.scheduled_date ?? workout.scheduled_week_start), 'dd/MM')
    : ''
  useBreadcrumb(
    () => ({
      onBack: () => navigate('/training-diary'),
      segments: [
        { label: dateShort || '…', onClick: () => navigate('/training-diary') },
        { label: workout?.exercise.title ?? '…', muted: true },
      ],
    }),
    [navigate, dateShort, workout?.exercise.title]
  )

  async function handleDelete() {
    if (!workout) return
    setDeleting(true)
    try {
      await workoutsService.destroy(workout.id)
      navigate('/training-diary')
    } finally {
      setDeleting(false)
      setDeleteDialogOpen(false)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-4 max-w-2xl mx-auto p-4">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-48 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    )
  }

  if (!workout) {
    return (
      <div className="flex flex-col gap-3 max-w-2xl mx-auto p-4 text-center">
        <p className="text-muted-foreground">{t('trainingDiary.workouts.detail.notFound', 'Workout not found')}</p>
        <Button onClick={() => navigate('/training-diary')}>
          {t('common.back', 'Back')}
        </Button>
      </div>
    )
  }

  const { exercise } = workout

  return (
    <div className="flex flex-col gap-5 max-w-2xl mx-auto p-4 pb-12">
      <header className="flex flex-col gap-2">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {exercise.category.name}
        </span>
        <h1 className="text-2xl font-semibold leading-tight">{exercise.title}</h1>
      </header>

      {exercise.image_url && (
        <button
          type="button"
          onClick={() => setImageOpen(true)}
          className="rounded-xl overflow-hidden bg-muted w-full max-h-72"
        >
          <img
            src={exercise.image_url}
            alt={exercise.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </button>
      )}

      {exercise.duration_minutes != null && (
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Clock className="size-4" />
          {formatDuration(exercise.duration_minutes, mPrefix, hPrefix)}
        </div>
      )}

      {exercise.description && (
        <button
          type="button"
          onClick={() => setDescExpanded((v) => !v)}
          className={cn(
            'text-sm text-foreground/90 text-left whitespace-pre-wrap',
            !descExpanded && 'line-clamp-5'
          )}
        >
          {exercise.description}
        </button>
      )}

      {exercise.templates && exercise.templates.length > 0 && (
        <section className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('workoutTimer.workoutDetail.templatesTitle', 'Timer protocol')}
            </h2>
            <Button type="button" size="sm" onClick={handleArmFullWorkout}>
              <Timer className="size-3 mr-1" />
              {t('workoutTimer.workoutDetail.startWorkout', 'Start timer')}
            </Button>
          </div>
          <ul className="flex flex-col gap-1.5">
            {exercise.templates.map((tpl) => (
              <li
                key={tpl.id}
                className="flex items-center gap-2 rounded-md border p-2 bg-card"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{tpl.title ?? '—'}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('workoutTimer.workoutDetail.summary', '{{sets}} sets × {{reps}} reps', {
                      sets: tpl.sets,
                      reps: tpl.reps_per_set,
                    })}
                    {tpl.work_seconds != null && ` · ${tpl.work_seconds}s`}
                    {tpl.reps_target != null && ` · ${tpl.reps_target} reps`}
                    {tpl.weight_percent != null && ` · ${tpl.weight_percent}%`}
                    {tpl.rpe != null && ` · RPE ${tpl.rpe}`}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => handleArmTemplate(tpl)}
                  aria-label={t('workoutTimer.workoutDetail.startTemplate', 'Start this template')}
                >
                  <Play className="size-3" />
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Results: list + "Record result" CTA (opens dedicated create page) */}
      <section className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('trainingDiary.workouts.detail.resultsTitle', 'Results')}
          </h2>
          <Button
            type="button"
            size="sm"
            onClick={() => navigate(`/training-diary/workouts/${workout.id}/results/new`)}
          >
            <Plus className="size-3 mr-1" />
            {t('trainingDiary.workouts.card.recordResultButton', 'Record result')}
          </Button>
        </div>
        {results.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('trainingDiary.workouts.detail.resultsEmpty', 'No results recorded yet for this exercise')}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {results.map((r) => (
              <ResultRow key={r.id} result={r} fromWorkoutId={workout.id} />
            ))}
          </div>
        )}
      </section>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start h-8 text-xs text-destructive hover:text-destructive"
        onClick={() => setDeleteDialogOpen(true)}
      >
        <Trash2 className="size-3 mr-1" />
        {t('trainingDiary.workouts.detail.deleteButton', 'Delete this workout')}
      </Button>

      {/* Image lightbox */}
      <Dialog open={imageOpen} onOpenChange={setImageOpen}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black border-0">
          {exercise.image_url && (
            <img
              src={exercise.image_url}
              alt={exercise.title}
              className="w-full h-auto object-contain"
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('trainingDiary.workouts.detail.deleteConfirmTitle', 'Delete this workout?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'trainingDiary.workouts.detail.deleteConfirmDescription',
                'This action removes the planned workout. Results remain untouched.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? t('common.deleting', 'Deleting…') : t('common.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
